import { useNavigate, useParams } from "react-router-dom";
import { routes } from "consts";
import React, { useEffect } from "react";
import { getApiErrorMessage, useToaster } from "utils";
import { useQuery } from "@tanstack/react-query";

import { PaziaLoader } from "components/atoms";
import { SurveyPreview } from "survey";
import { HeaderWithBreadcrumbs, PageMain } from "components/molecules";
import { SurveyApi, surveyApi } from "api/surveyApi";
import { SurveyProvider, useSurvey } from "pages/SurveyCreate/store/reducer";
import { ShSurveyCreator } from "pages/SurveyCreate/ShSurveyCreator";
import { RatingQuestionFormValues } from "pages/SurveyCreate/Question/forms/RatingQuestionForm";
import { TextQuestionFormValues } from "pages/SurveyCreate/Question/forms/TextQuestionForm";
import { SelectQuestionFormValues } from "pages/SurveyCreate/Question/forms/SelectQuestionForm";
import { QUERIES_KEYS } from "queries";
import { queryClient } from "main";
import { savedQuestionIds } from "pages/SurveyCreate/questionOrder";

export const SurveyPagesPage = () => {
  const { surveyId } = useParams();
  const { isLoading, data } = useQuery({
    queryKey: QUERIES_KEYS.SINGLE_SURVEY(surveyId!),
    queryFn: () => surveyApi.fetchById(Number(surveyId)),
    enabled: !!surveyId,
  });
  if (isLoading) return <PaziaLoader position="center" />;
  if (!data?.data) return null;
  return (
    <SurveyProvider>
      <Page survey={data.data} />
    </SurveyProvider>
  );
};

const Page = ({ survey }: { survey: SurveyApi }) => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToaster();

  useEffect(() => {
    dispatch({ type: "INITIALIZE_SURVEY", payload: survey.questions });
  }, []);
  const {
    state: { questions, previewQuestionIdx },
    dispatch,
  } = useSurvey();

  const moveNext = () => dispatch({ type: "SET_PREVIEW_QUESTION_IDX", payload: previewQuestionIdx + 1 });
  const moveBack =
    previewQuestionIdx === 0
      ? undefined
      : () => dispatch({ type: "SET_PREVIEW_QUESTION_IDX", payload: previewQuestionIdx - 1 });
  const breadcrumbs = [{ title: "Ankiety", path: routes.SURVEYS }, { title: survey.name }];

  const updateQuestion = async (data: RatingQuestionFormValues | TextQuestionFormValues | SelectQuestionFormValues) => {
    try {
      // @ts-ignore
      await surveyApi.updateQuestion(Number(surveyId), data.id, data);
    } catch (error) {
      console.error(error);
    }
  };

  const createQuestion = async (
    data: RatingQuestionFormValues | TextQuestionFormValues | SelectQuestionFormValues,
  ): Promise<number> => {
    try {
      // @ts-ignore
      const response = await surveyApi.createQuestion(Number(surveyId), data);
      return response.data;
    } catch (error) {
      console.error(error);
      return 0;
    }
  };

  const reorderQuestions = async (questionIds: number[]) => {
    try {
      await surveyApi.reorderQuestions(Number(surveyId), questionIds);
    } catch (error) {
      showError(getApiErrorMessage(error, "Nie udało się zapisać kolejności pytań."));
      throw error;
    }
  };

  const onSubmit = async (data: RatingQuestionFormValues | TextQuestionFormValues | SelectQuestionFormValues) => {
    let id = Number(data.id);
    if (data.id) await updateQuestion(data);
    else {
      id = await createQuestion(data);
      // the API appends a new question; if it was moved up before its first save, put it where it is on screen
      const ids = savedQuestionIds(questions.map((q) => (q.index === Number(data.index) ? { id } : q)));
      if (id && ids[ids.length - 1] !== id) await reorderQuestions(ids).catch(() => undefined);
    }

    queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.SINGLE_SURVEY(surveyId!) });
    return id;
  };

  const onDeleteQuestion = async (id: number) => {
    try {
      await surveyApi.deleteQuestion(Number(surveyId), id);
      queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.SINGLE_SURVEY(surveyId!) });
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <PageMain>
      <HeaderWithBreadcrumbs breadcrumbs={breadcrumbs} headerText="Edytuj ankietę" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-10">
        <ShSurveyCreator
          options={["free-text", "multi-select", "rating", "single-select", "course-rating", "trainer-rating"]}
          onSubmit={onSubmit}
          onDeleteQuestion={onDeleteQuestion}
          onReorder={reorderQuestions}
        />
        <SurveyPreview
          previewQuestionIdx={previewQuestionIdx}
          moveBack={moveBack}
          moveNext={moveNext}
          questions={questions}
        />
      </div>
    </PageMain>
  );
};
